import React, { useEffect, useState } from 'react';
import { Activity, DollarSign, TrendingUp } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import EmptyState from '../../../components/common/EmptyState';
import StatusChip from '../components/shared/StatusChip';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { DataTable } from '../../../components/ui/data-table';
import { pammTradeService } from '../services/pammService';
import {
  formatCurrency,
  formatDateTime,
  getProfitColor,
  exportToCSV,
} from '../utils';
import type { PAMMTrade } from '../types';
import { PAMM_TRADE_DIRECTION_OPTIONS } from '../constants';
import type { DataTableColumn } from '../../../components/ui/data-table';

export default function OpenTrades() {
  const [trades, setTrades] = useState<PAMMTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setTrades(await pammTradeService.getOpen());
    } catch {
      setError('Failed to load open trades.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  const totalProfit = trades.reduce((s, t) => s + (t.profit ?? 0), 0);
  const buyTrades = trades.filter(t => t.direction === 'buy');
  const sellTrades = trades.filter(t => t.direction === 'sell');

  const columns: DataTableColumn<PAMMTrade>[] = [
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: row => (
        <span className="font-mono font-medium text-xs">{row.symbol}</span>
      ),
      sortable: true,
      sortValue: row => row.symbol,
    },
    {
      id: 'fund',
      header: 'Fund',
      accessor: row => <span className="text-xs">{row.fundName}</span>,
    },
    {
      id: 'direction',
      header: 'Direction',
      accessor: row => <StatusChip status={row.direction} size="sm" />,
    },
    {
      id: 'volume',
      header: 'Volume',
      accessor: row => <span className="text-xs">{row.volume} lots</span>,
      sortable: true,
      sortValue: row => row.volume,
    },
    {
      id: 'openPrice',
      header: 'Open Price',
      accessor: row => (
        <span className="text-xs font-mono">{row.openPrice.toFixed(5)}</span>
      ),
    },
    {
      id: 'currentPrice',
      header: 'Current',
      accessor: row => (
        <span className="text-xs font-mono">
          {row.currentPrice?.toFixed(5) ?? '—'}
        </span>
      ),
    },
    {
      id: 'profit',
      header: 'Floating P&L',
      accessor: row => (
        <span
          className={`text-xs font-semibold ${getProfitColor(row.profit ?? 0)}`}
        >
          {formatCurrency(row.profit ?? 0)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.profit ?? 0,
    },
    {
      id: 'sl',
      header: 'S/L',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {row.stopLoss?.toFixed(5) ?? '—'}
        </span>
      ),
    },
    {
      id: 'tp',
      header: 'T/P',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {row.takeProfit?.toFixed(5) ?? '—'}
        </span>
      ),
    },
    {
      id: 'openTime',
      header: 'Opened',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {formatDateTime(row.openTime)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.openTime,
    },
  ];

  if (loading)
    return (
      <div className="space-y-4">
        <PageHeader title="Open Trades" />
        <LoadingSkeleton type="table" />
      </div>
    );
  if (error)
    return (
      <div className="space-y-4">
        <PageHeader title="Open Trades" />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="Open Trades"
        description="Monitor all currently open PAMM trading positions"
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Open Positions"
          value={trades.length}
          icon={Activity}
        />
        <StatsCard
          title="Buy Trades"
          value={buyTrades.length}
          icon={TrendingUp}
        />
        <StatsCard
          title="Sell Trades"
          value={sellTrades.length}
          icon={TrendingUp}
        />
        <StatsCard
          title="Floating P&L"
          value={formatCurrency(totalProfit, 'USD', true)}
          icon={DollarSign}
        />
      </div>
      {trades.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="No Open Trades"
          description="Open trading positions will appear here."
        />
      ) : (
        <DataTable
          columns={columns}
          data={trades}
          searchValue={searchValue}
          showColumnVisibility={true}
          searchPlaceholder="Search trades..."
          onSearchChange={setSearchValue}
          onSearchReset={() => setSearchValue('')}
          filterFields={[
            {
              id: 'direction',
              label: 'Direction',
              options: PAMM_TRADE_DIRECTION_OPTIONS,
            },
          ]}
          activeFilters={activeFilters}
          onFiltersChange={setActiveFilters}
          showExportButton={true}
          exportButtonText="Export CSV"
          onExportClick={() => exportToCSV(trades, 'open_trades')}
        />
      )}
    </div>
  );
}
